import React, { useEffect, useState, useRef, Suspense, lazy} from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams} from 'react-router-dom';
import { QueryClientProvider, QueryClient} from '@tanstack/react-query';
import { TyndoProvider, HubConnectionProvider, PermissionsProvider, useAuth, useTenantContext, useCurrentUser} from '@tyndo/hooks';
import { Button, _registerListArchetypeIdentityHook } from '@tyndo/ui-kit';
import AppShell from './layout/AppShell';
import ModuleView from './layout/ModuleView';
import HomePage from './pages/HomePage';
import PersonaWorkspace from './pages/PersonaWorkspace';
import PlantCanvas from './pages/PlantCanvas';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import SignupPage from './pages/SignupPage';
import VerifyPage from './pages/VerifyPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { LandingPageEditor } from './components/LandingPageEditor';
import { fetchWithTimeoutRetry } from './utils/fetchWithTimeoutRetry';

// Widget administration console — scheduled reports, alert rules, snapshots,
// definition export/import, and the governed-action audit trail. Lazy so it
// stays out of the initial shell bundle.
const WidgetsAdminConsole = lazy(() =>
 import('./components/WidgetsAdminConsole').then((m) => ({ default: m.WidgetsAdminConsole })),
);

// Client-side role guard for the admin-only /widgets-admin surface. Hiding the
// menu link is not enough — a direct navigation to the URL must not render the
// admin console. Mirrors the TopBar admin-role check; the backend WidgetsAdmin
// policy remains the authoritative control (this is defense-in-depth). A
// non-admin is redirected to the landing route rather than shown the console.
function isWidgetsAdmin(roles: string[]): boolean {
 return (
 roles.includes('Administrator') ||
 roles.includes('admin') ||
 roles.some((r) => typeof r === 'string' && r.startsWith('widgets.'))
 );
}

const WidgetsAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
 const roles = useAuthRoles();
 if (!isWidgetsAdmin(roles)) {
 return <Navigate to="/" replace />;
}
 return <>{children}</>;
};
import {
 viewRegistry,
 menuRegistry,
 actionRegistry,
 widgetRegistry,
 moduleManifestRegistry,
 approvalProviderRegistry,
 moduleRegistration,
} from './registries';
// Shell-owned cross-module "My Approvals" surface (UI-3). Registered below as a
// synthetic `tyndo.approvals` module so it flows through the same menu/view/
// action registries + ModuleView route resolution as a first-class module view.
import MyApprovals from './pages/MyApprovals';
import { registerApprovalsShellModule } from './registries/registerApprovalsShellModule';
import type {
  ApprovalQueueProvider,
  UiMenuItem,
  UiAction,
  UiViewDefinition,
  UiWidget,
} from '@tyndo/types';
import { RealtimeInvalidationBridge } from './realtime/RealtimeInvalidationBridge';
import { toast } from '@tyndo/ui-kit';
import ShellErrorBoundary, { consumeShellCrashContext } from './components/ShellErrorBoundary';
// Compiled-in (monolith) module loaders — preferred over the federated remote
// when a module has been migrated off federation. See ./registries/moduleLoaders
// and docs/todo.md G1559–G1565.
import { getLocalLoader } from './registries/moduleLoaders';
import {
 parseManifestModules,
 parseLoadedModule,
 resolveViewLoader,
 resolveDefaultViewIndex,
 type ManifestModuleEntry,
 type ManifestView,
} from './registries/manifestSchema';
import { recordEvent, recordError } from './observability/telemetry';
import { remoteHealth } from './observability/remoteHealth';
import { icons as lucideIcons } from 'lucide-react';

// Wire a lucide-react icon-name validator into the manifest registry so a
// typo'd manifest icon surfaces a console warning at registration rather than
// silently falling back to a neutral nav marker. The shell owns lucide-react,
// so the validator lives here and the registry stays dependency-free.
moduleManifestRegistry.setIconValidator(
 (name: string) => Object.prototype.hasOwnProperty.call(lucideIcons, name),
);
import { useProactiveTokenRefresh } from './auth/useProactiveTokenRefresh';
import { useAuthRoles } from './auth/useSyncAuthRoles';
import { useLocaleBootstrap } from './useLocaleBootstrap';
import { EbrRouteBridge, ModuleRedirect } from './routes/routeBridges';
import { isDevBuild, CLIENT_FALLBACK_MODULES, resolveFallbackModuleAllowlist } from './config/clientFallbackModules';

// Namespace the list-archetype's persisted preferences (saved filters,
// column visibility, page size) by the current tenant + user so one user's
// settings don't leak to another on a shared browser. The registered fn is
// invoked as a hook inside `useListArchetype`, which always renders inside
// the auth/permissions provider tree.
_registerListArchetypeIdentityHook(() => {
 const profile = useCurrentUser();
 if (!profile) return null;
 return { userId: profile.sub, tenantId: profile.tenantId };
});


const queryClient = new QueryClient({
 defaultOptions: {
 queries: {
 // H-6 freshness defaults: 30 s, not the old 5 min. The realtime
 // invalidation bridge keeps event-covered resources live, but anything
 // outside its event→resource map could sit visibly stale for minutes on
 // an operator station. 30 s still dedups burst navigation (TanStack
 // shares in-flight requests per key regardless) without lying for long.
 staleTime: 1000 * 30, // 30 seconds
 gcTime: 1000 * 60 * 10, // 10 minutes
 // Fail fast — a missing endpoint shouldn't block module pages for ~7s
 // (default 3 retries × 1s backoff). One quick retry is plenty.
 retry: 1,
 retryDelay: 500,
 // Re-fetch whenever the tab regains focus (H-6): an operator returning
 // from another HMI window must see current state immediately, even for
 // resources the realtime bridge has no event mapping for. 'always'
 // refetches on focus even inside the 30 s staleTime window.
 refetchOnWindowFocus: 'always',
 },
 mutations: {
 retry: 0,
 },
 },
});

// Federated remote loaders. The return type is `Promise<unknown>` (not `any`):
// the loaded bundle is validated through `parseLoadedModule` at the seam, so
// the federation-boundary value stays opaque to the rest of the shell rather
// than leaking `any` into every downstream consumer.
const federatedLoaders: Record<string, () => Promise<unknown>> = {
  // Only the runtime-plugin surface stays federated (G1562/G1563): the
  // Marketplace + customer-authored widgets/modules load as separately-
  // versioned remotes at runtime. Every first-party module is compiled into
  // the shell as a lazy chunk via `localLoaders` (./registries/moduleLoaders).
  marketplace_ui: () => import('marketplace_ui/module'),
};

// A view/widget component resolved across the federation boundary. Props are
// unknown at this seam (each module owns its own prop contract), so we type it
// as an open component the registries can render with the shell-supplied props.
type LoadedComponent = React.ComponentType<Record<string, unknown>>;

// Federation returns either a component module (`{ default: Component }`) or the
// bare component. Normalise both to the `{ default }` shape the registries load.
// The resolved value is asserted to a component at this validated seam rather
// than leaking `any` into every downstream loader.
function toDefaultExport(loaded: unknown): { default: LoadedComponent } {
  const mod = loaded as { default?: LoadedComponent };
  return { default: (mod?.default ?? loaded) as LoadedComponent };
}


/**
 * Generic dashboard route — `/dashboard/:routeKey`. Reuses the same
 * landing-page editor shell. The default body is the Plant Canvas so a
 * fresh dashboard renders a sensible scene before any widgets are added;
 * once the route key has saved widgets, the editor's "view" mode will
 * surface them above the seed page. Future iterations can swap the seed
 * out for a layout-driven renderer.
 */
function DashboardRoute() {
 const { routeKey} = useParams<{ routeKey: string}>();
 const key = routeKey ?? 'dashboard';
 return (
 <LandingPageEditor routeKey={key}>
 <PlantCanvas />
 </LandingPageEditor>
 );
}

function AppContent() {
 const { isAuthenticated, accessToken} = useAuth();
 const { tenantId } = useTenantContext();
 const [manifestLoaded, setManifestLoaded] = useState(false);
 const [manifestError, setManifestError] = useState<string | null>(null);
 // Realtime subscription descriptors collected from the UI manifest; consumed
 // by the RealtimeInvalidationBridge. State (not a ref) so the bridge
 // re-subscribes when a module's manifest adds/removes a hub method.
 // Loosely typed (unknown[]) to match the manifest's ManifestModuleEntry
 // payload; the bridge narrows each descriptor defensively.
 const [realtimeSubscriptions, setRealtimeSubscriptions] = useState<unknown[]>(
 [],
 );
 // Active tenant, read lazily by the realtime→query-invalidation bridge so it
 // always invalidates within the current tenant without re-wiring on a token
 // refresh. The provider tears its sockets down on a tenant switch anyway.
 const tenantIdRef = useRef<string | null>(tenantId);
 tenantIdRef.current = tenantId;

 // If the previous session ended in a shell crash + recovery reload, the
 // boundary persisted the crash context to sessionStorage. Surface a one-time
 // notice (and re-record telemetry) so the operator knows the app recovered
 // rather than silently landing them somewhere with no trace of what failed.
 useEffect(() => {
 const crash = consumeShellCrashContext();
 if (!crash) return;
 recordError('shell.recovered_from_crash', new Error(crash.message), {
 scope: crash.scope,
 path: crash.path ?? undefined,
 crashedAt: crash.at,
 });
 toast.error('Tyndo recovered from an unexpected error.', {
 description: crash.message,
 });
 }, []);

 // Keep the access token fresh — refresh just before the decoded JWT exp so a
 // long operator session isn't booted to /login mid-task on expiry.
 useProactiveTokenRefresh();
 // Resolve the active locale from the user/tenant context (OIDC `locale` claim
 // > tenant default > browser), clamped to the locales that ship a bundle, and
 // apply it to i18n + the non-hook formatter resolver.
 useLocaleBootstrap();
 // Bumped after Pass 2 (federated module bundles register their full menu
 // trees). Without this AppShell would render once with the Pass 1 stubs
 // (top-level module names only) and never again until the user navigates,
 // so the sidebar at the landing page never gained child views. Only the
 // setter is held — the bump itself forces the re-render; nothing reads the
 // counter value.
 const [, setManifestVersion] = useState(0);

 useEffect(() => {
 const loadManifest = async () => {
 try {
 // Manifest is a public endpoint — no auth header needed
 const headers: Record<string, string> = {};
 if (accessToken) {
 headers['Authorization'] = `Bearer ${accessToken}`;
}
 // Scope the manifest to the active tenant so a tenant switch reloads
 // the correct module set (and the gateway can apply per-tenant
 // entitlements). Without this the prior tenant's manifest would persist.
 if (tenantId) {
 headers['X-Tenant-Id'] = tenantId;
}

 const response = await fetchWithTimeoutRetry(
 '/api/v1/ui/manifest',
 { headers },
 { timeoutMs: 8000, retries: 2 }
 );

 if (!response.ok) {
 throw new Error(`Failed to load manifest: ${response.statusText}`);
}

 const manifest: unknown = await response.json();

 // Clear existing registries
 viewRegistry.clear();
 menuRegistry.clear();
 actionRegistry.clear();
 widgetRegistry.clear();
 moduleManifestRegistry.clear();
 // Drop per-module approval providers too so a tenant switch can't leak the
 // prior tenant's aggregated approval queue into the new session. Modules
 // re-register their providers on the next federated load (Pass 2).
 approvalProviderRegistry.clear();
 // No module is registering yet for this (re)load; the Pass-2 dispatch below
 // marks each one pending before it fans out.
 moduleRegistration.reset();

 // Gateway returns: { modules: [{ moduleId, name, federatedModuleName, ...}]}
 // Parse the wire document against the hand-rolled ManifestSchema so a
 // malformed entry (missing moduleId, non-array menus, …) is rejected and
 // logged at load time instead of surfacing later as an opaque render crash.
 // For each valid module the shell dynamically loads its federated `./module`
 // export; the loaded module's default export is its own manifest with REAL
 // menus/views/actions — we ignore the synthetic ones from the backend.
 const { modules: gatewayModules, rejected, shapeWarning } = parseManifestModules(manifest);
 if (shapeWarning) {
 // The manifest didn't arrive in the canonical `{ modules: [...] }` shape.
 // parseManifestModules already fell back to `data` / a bare array where it
 // could; log a warning with actionable text so an unexpected gateway shape
 // is visible to operators rather than silently rendering an empty nav — and
 // never crashes the shell.
 recordEvent('manifest.unexpected_shape', 'warn', { detail: shapeWarning });
 }
 if (rejected.length) {
 recordError(
 'manifest.invalid_module_entries',
 new Error(`Manifest contained ${rejected.length} invalid module entr${rejected.length === 1 ? 'y' : 'ies'}`),
 { reasons: rejected.join('; ') }
 );
 }

 // In development only, stitch in client-side fallback entries for modules
 // whose federated bundle exists but the gateway manifest doesn't list yet
 // (CRM/Finance/HR/Sales/Multisite are still being plumbed through the
 // backend). In production the nav MUST reflect only the gateway-entitled
 // module list — injecting fallbacks there would surface modules the
 // gateway's per-tenant entitlement filter intentionally excluded.
 const knownFederatedNames = new Set(gatewayModules.map((m) => m.federatedModuleName));
 // The built-in fallback set can be narrowed at boot via the
 // TYNDO_FALLBACK_MODULES env override (comma-separated moduleIds) so a team
 // can opt a specific module out of the dev fallback without editing source.
 // Unset → the full built-in list (previous behaviour). Never applies in
 // production (the nav must reflect only the gateway-entitled list).
 const allowedFallbackIds = resolveFallbackModuleAllowlist();
 const fallbackModules: ManifestModuleEntry[] = isDevBuild
 ? CLIENT_FALLBACK_MODULES.filter(
 (m) =>
 !knownFederatedNames.has(m.federatedModuleName) &&
 (allowedFallbackIds === null || allowedFallbackIds.has(m.moduleId))
 ).map((m) => ({
 ...m,
 realtimeSubscriptions: [],
 }))
 : [];
 const modules: ManifestModuleEntry[] = [...gatewayModules, ...fallbackModules];

 // Dev-only: tell operators exactly which modules came from the client-side
 // fallback (i.e. are NOT in the gateway's entitled manifest yet) so a missing
 // backend manifest entry is visible rather than mistaken for a real module.
 if (isDevBuild && fallbackModules.length) {
 recordEvent('manifest.client_fallback_modules', 'info', {
 moduleIds: fallbackModules.map((m) => m.moduleId).join(', '),
 count: fallbackModules.length,
 });
 console.info(
 `[tyndo] ${fallbackModules.length} module(s) loaded from CLIENT fallback (not in the gateway manifest): ` +
 fallbackModules.map((m) => m.moduleId).join(', '),
 );
 }

 // Capture the full module list so HomePage / Plant Canvas / ⌘K can render
 // a coherent module catalogue without hardcoding or re-fetching.
 moduleManifestRegistry.register(modules);

 // Collect every module's realtime subscription descriptors so the
 // RealtimeInvalidationBridge subscribes to the right per-hub method list
 // on the shared provider sockets.
 // parseManifestModules normalised realtimeSubscriptions to an array per module.
 setRealtimeSubscriptions(
 modules.flatMap((m) => m.realtimeSubscriptions ?? [])
 );

 // PASS 1 — register manifest-provided menus/views/actions IMMEDIATELY so the
 // sidebar populates instantly even if federated bundles fail to load.
 // Each view's loader will lazily attempt federation when the user navigates.
 modules.forEach((mod) => {
 const moduleId = mod.moduleId;
 const moduleName = mod.name;
 const federatedName = mod.federatedModuleName;

 // parseManifestModules already normalised these to arrays.
 const manifestMenus = mod.menus;
 const manifestViews = mod.views;
 const manifestActions = mod.actions;

 if (manifestMenus.length) {
 // Validated wire menus → the registry's UiMenuItem DTO. The manifest shape
 // omits DTO-required fields (id/path/order), so the assertion goes via
 // `unknown` per the compiler; downstream is then fully UiMenuItem-checked.
 menuRegistry.register(moduleId, manifestMenus as unknown as UiMenuItem[]);
}
 if (manifestActions.length) {
 actionRegistry.register(moduleId, manifestActions as UiAction[]);
}

 // Register view stubs whose loader does federation on demand.
 // The default view is resolved deterministically (module-declared
 // isDefault → dashboard/overview name → first menu entry → index 0)
 // instead of blindly stamping the first array entry as the landing view.
 const defaultViewIdx = resolveDefaultViewIndex(
 manifestViews,
 manifestMenus,
 manifestActions,
 );
 manifestViews.forEach((vd: ManifestView, idx: number) => {
 const viewDef = {
 ...vd,
 model: moduleId,
 viewType: vd.viewType ?? 'view',
 isDefault: idx === defaultViewIdx,
 moduleId,
 moduleName,
};
 const loader = () => {
 // Prefer a compiled-in (monolith) loader when this module has been migrated
 // off federation; otherwise fall back to the federated remote. Enables the
 // incremental, always-green MF→single-app migration (docs/todo.md G1559–G1565).
 const loaderFn =
 getLocalLoader(federatedName) ??
 (federatedName ? federatedLoaders[federatedName] : undefined);
 if (!loaderFn) {
 return Promise.reject(
 new Error(
 `Module ${moduleId} federated bundle not configured (federatedName=${federatedName ?? '? '})`
 )
 );
}
 return Promise.resolve(loaderFn()).then((loaded: unknown) => {
 // Validate the loaded bundle against the ModuleLoadedShape union so a
 // shape mismatch is a precise, branchable error rather than a downstream
 // "x.views is not a function".
 const shape = parseLoadedModule(loaded);
 if (shape.kind === 'invalid') {
 return Promise.reject(
 new Error(`Module ${moduleId} bundle is malformed: ${shape.reason}`)
 );
 }
 const fn = resolveViewLoader(
 shape.viewLoaders,
 vd.componentName,
 shape.kind === 'manifest' ? shape.views : [],
 idx
 );
 if (!fn) {
 return Promise.reject(
 new Error(
 `Module ${moduleId} does not export a view component (manifest asked for '${vd.componentName ?? '?'}', module exports: ${Object.keys(shape.viewLoaders).join(', ') || 'none'})`
 )
 );
 }
 return Promise.resolve(fn()).then(toDefaultExport);
 });
 };
 viewRegistry.register(viewDef as UiViewDefinition, loader);
});
});

 // Register the shell-owned "My Approvals" surface (UI-3) as a synthetic
 // module so it gets a nav entry + a ModuleView-resolvable route alongside the
 // real modules. Runs after the module loop so it survives the registry clear
 // at the top of each (re)load; its view loader returns the local component
 // directly (no federation), and the individual module approval providers
 // register progressively during Pass 2.
 registerApprovalsShellModule(MyApprovals as React.ComponentType<Record<string, unknown>>);

 // PASS 2 — try to upgrade with federated module data in the background.
 // If federation fails (module dev server down, bundle 404), no problem —
 // PASS 1 data is still in the registries.
 //
 // Dedupe by federatedModuleName: each federated bundle should map to ONE
 // canonical moduleId. If two gateway entries share a federatedName, only
 // load the bundle once and register its menus under the first matching
 // moduleId. Without this, admin_ui's menus (HMI Designer, Users, etc.)
 // would be re-registered under every phantom moduleId that aliased
 // admin_ui, with last-write-wins flipping the eyebrow label unpredictably.
 const seenFederatedNames = new Set<string>();
 const uniqueModules = modules.filter((mod) => {
 const fed = mod.federatedModuleName;
 if (!fed) return true;
 if (seenFederatedNames.has(fed)) return false;
 seenFederatedNames.add(fed);
 return true;
 });

 // Declare every federated module's registration in flight BEFORE the splash
 // comes down. ModuleView mounts the instant `manifestLoaded` flips, and a
 // deep link opened at that moment resolves nothing until its own module has
 // registered — marking pending first is what lets ModuleView tell "still
 // registering" apart from "registered, and there is no such view". Doing it
 // after the flip would leave a window in which the pending set is empty and
 // the route would render the settled error state for a module that had not
 // been asked for yet.
 for (const mod of uniqueModules) {
 if (mod.federatedModuleName) moduleRegistration.begin(mod.moduleId);
 }

 // Sidebar can render now — flip the loaded flag so the shell unmounts the splash.
 setManifestLoaded(true);
 // Per-module federated upgrade. Extracted so it can be re-invoked by the
 // remote-health retry (manual button + background scheduler) without
 // reloading the whole app: a SCADA/Production remote whose dev server was
 // briefly down self-heals on the next attempt.
 const loadFederatedModule = async (mod: ManifestModuleEntry): Promise<void> => {
 const moduleId = mod.moduleId;
 const moduleName = mod.name;
 const federatedName = mod.federatedModuleName;
 if (!federatedName) return;

 // This module's registration is in flight from here. Until it settles, a
 // route that resolves no component for this module is UNDECIDED, not wrong —
 // ModuleView reads this to render a registering state instead of the
 // "no component is wired up" verdict. `deferred` marks the one path that
 // intentionally leaves the flag set: an over-budget import is still coming
 // and re-enters this function when it lands.
 moduleRegistration.begin(moduleId);
 let deferred = false;

 // Prefer the compiled-in local loader (single-image / consolidated build);
 // fall back to the federated remote only when no local chunk exists. Without
 // this the startup manifest load probed dead dev-default remoteEntry URLs and
 // marked every module "unreachable" even though it ships as a local chunk.
 const loaderFn = getLocalLoader(federatedName) ?? federatedLoaders[federatedName];
 if (!loaderFn) {
 remoteHealth.markFailed(
 federatedName,
 new Error('No federated loader configured'),
 moduleId
 );
 moduleRegistration.settle(moduleId);
 return;
}

 try {
 // Cap each remote's load. Pass 2 fans these out with Promise.all, so a
 // single hung dev server / never-resolving remoteEntry would otherwise
 // block the whole manifest upgrade indefinitely. Overrunning the budget
 // goes through markFailed (classified 'unreachable'), marking the remote
 // degraded so the nav badge + auto-retry loop take over.
 //
 // Missing the budget marks the remote degraded; it does NOT abandon the
 // import. The two are different facts: "this is taking too long to show
 // the operator a healthy module" and "this will never arrive". Treating
 // the first as the second is what made a cold start unrecoverable — with
 // ~60 modules fanned out at once, every budget starts on the same tick, a
 // cold bundler misses all of them, the auto-retry spends its whole
 // attempt allowance inside the same cold window, and the shell then never
 // looks again: the module is absent for the life of the tab even though
 // its chunk landed seconds later. Keeping a handle on the original import
 // and re-entering when it settles means a late module registers itself
 // (and clears its own badge via markOk) with no further attempt budget.
 const FEDERATION_LOAD_TIMEOUT_MS = 15000;
 const OVER_BUDGET = Symbol('over-budget');
 const load: Promise<unknown> = Promise.resolve(loaderFn());
 const loadTimeout = new Promise<typeof OVER_BUDGET>((resolve) =>
 setTimeout(() => resolve(OVER_BUDGET), FEDERATION_LOAD_TIMEOUT_MS)
 );
 const raced = await Promise.race([load, loadTimeout]);
 if (raced === OVER_BUDGET) {
 remoteHealth.markFailed(
 federatedName,
 new Error(
 `Timeout loading federated module ${federatedName} (${moduleId})`
 ),
 moduleId
 );
 // Deliberately not awaited: the Pass-2 fan-out must stay bounded, so a
 // genuinely never-settling import still can't stall the whole upgrade.
 // Re-entering rather than duplicating the registration body means the
 // late arrival takes the exact same path — the import is resolved by
 // then, so the second attempt wins its own race immediately.
 deferred = true;
 void load.then(
 () => loadFederatedModule(mod),
 (err) => {
 remoteHealth.markFailed(federatedName, err, moduleId);
 // Terminal for this module: the import that was still outstanding has
 // now rejected, so there is nothing left to wait for.
 moduleRegistration.settle(moduleId);
 }
 );
 return;
}
 const loaded: unknown = raced;
 if (!loaded) {
 remoteHealth.markFailed(
 federatedName,
 new Error('Federated remote returned null'),
 moduleId
 );
 return;
}

 // Validate the loaded bundle against the ModuleLoadedShape discriminated
 // union. Module Federation flattens the `{ default, views, … }` export in
 // several shapes; the validator finds the manifest among the candidates,
 // resolves the lazy view/widget loader maps, and reports `empty`/`invalid`
 // so a shape mismatch is caught here rather than via a downstream crash.
 const shape = parseLoadedModule(loaded);
 if (shape.kind === 'invalid') {
 remoteHealth.markFailed(
 federatedName,
 new Error(`Federated remote malformed: ${shape.reason}`),
 moduleId
 );
 return;
}

 const views: ManifestView[] = shape.kind === 'manifest' ? shape.views : [];
 const menus = shape.kind === 'manifest' ? shape.menus : [];
 const actions = shape.kind === 'manifest' ? shape.actions : [];
 const widgets = shape.kind === 'manifest' ? shape.widgets : [];
 const viewLoaders = shape.viewLoaders;
 const widgetLoaders = shape.widgetLoaders;

 recordEvent('federation.debug', 'info', {
 federatedName,
 viewLoaderKeys: Object.keys(viewLoaders),
 viewDescriptors: views.length,
 loadedShape: shape.kind,
 });

 if (shape.kind === 'empty') {
 // Remote loaded but exposed nothing usable — surface it as a degraded
 // (empty) remote rather than letting it silently no-op.
 remoteHealth.markEmpty(federatedName, moduleId);
}

 // The federated module is the source of truth. Register its real menus in
 // 'replace' mode so Pass 1 stubs (from the gateway manifest) are dropped
 // explicitly — we don't end up with both `tyndo.scada.root` (stub) and
 // `scada.root` (federated) at top level.
 // viewRegistry / actionRegistry don't have remove yet; the next register
 // calls will overwrite by key for actions, and view stubs whose keys
 // overlap will be replaced. Stub-only entries that aren't replaced will
 // simply still be navigable via the lazy federation loader.
 if (menus.length) {
 menuRegistry.register(moduleId, menus as unknown as UiMenuItem[], 'replace');
} else if (views.length) {
 // Federated views but no menus: still drop this module's stale stub menus
 // so they don't double the nav.
 menuRegistry.removeForModule(moduleId);
}
 if (actions.length) {
 actionRegistry.register(moduleId, actions as UiAction[]);
}

 // Register views. The module's ViewDescriptor doesn't include `model`/`viewType`,
 // so we synthesize them so the shell's existing route resolver works:
 // route: /:moduleId/:viewType/:model
 // key: `${model}:${viewType}:${viewId}`
 // We use moduleId as model and'view' as viewType. The default view is
 // resolved deterministically (module-declared isDefault → dashboard/
 // overview name → first menu entry → index 0) so clicking a module lands
 // on its genuine landing surface rather than an arbitrary first view.
 const federatedDefaultIdx = resolveDefaultViewIndex(views, menus, actions);
 views.forEach((vd: ManifestView, idx: number) => {
 const viewDef = {
 ...vd,
 model: moduleId,
 viewType:'view',
 isDefault: idx === federatedDefaultIdx,
 moduleId,
 moduleName,
};
 const loader = () => {
 // Manifest-provided componentName is sometimes a synthesized 'default'
 // (when the backend module declares no UI manifest). resolveViewLoader
 // applies the shell's fallback chain: exact componentName → the real
 // descriptor name at this index → the loader map's key at this index →
 // its first key.
 const fn = resolveViewLoader(viewLoaders, vd.componentName, views, idx);
 if (!fn) {
 return Promise.reject(
 new Error(
 `Module ${moduleId} does not export view'${vd.componentName ?? '?'}' (available: ${Object.keys(viewLoaders).join(', ') || 'none'})`
 )
 );
}
 recordEvent('federation.view_load', 'info', { moduleId, componentName: vd.componentName });
 // Module-side `views[name]()` returns Promise<Component>; wrap it in { default}
 const result = Promise.resolve(fn()).then(toDefaultExport);
 // Add a timeout so hangs become visible errors
 const timeout = new Promise<never>((_, reject) =>
 setTimeout(() => reject(new Error(
 `Timeout loading view ${vd.componentName} from ${moduleId}`
 )), 10000)
 );
 return Promise.race([result, timeout]);
};
 viewRegistry.register(viewDef as UiViewDefinition, loader);
});

 // Register widgets if any. Manifest widget descriptors are loosely typed at
 // the wire (they carry a `componentName` the loader map is keyed by), so we
 // narrow to that descriptor shape and assert the registered definition to the
 // strict UiWidget DTO at this boundary.
 (widgets as Array<{ componentName: string; [key: string]: unknown }>).forEach((wd) => {
 const widgetDef = { ...wd, moduleId};
 const loader = () => {
 const fn = widgetLoaders[wd.componentName];
 if (typeof fn !=='function') {
 return Promise.reject(
 new Error(
 `Module ${moduleId} does not export widget'${wd.componentName}'`
 )
 );
}
 return Promise.resolve(fn()).then(toDefaultExport);
};
 widgetRegistry.register(widgetDef as unknown as UiWidget, loader);
});

 // Cross-module "My Approvals" provider (UI-3). A module opts in by exporting
 // an `approvalProvider` (an ApprovalQueueProvider) from its module entry; the
 // shell registers it so the module's pending approvals fold into the
 // aggregated inbox. Read defensively — most modules don't export one, and a
 // malformed export must never break federation of the module's views.
 const maybeProvider = (loaded as { approvalProvider?: unknown }).approvalProvider;
 if (
 maybeProvider &&
 typeof maybeProvider === 'object' &&
 typeof (maybeProvider as { useApprovals?: unknown }).useApprovals === 'function'
 ) {
 const provider = maybeProvider as ApprovalQueueProvider;
 // Trust the module's own moduleId on the provider, falling back to the
 // manifest moduleId so a mis-set field can't orphan the provider.
 approvalProviderRegistry.register({ ...provider, moduleId: provider.moduleId || moduleId });
 recordEvent('approvals.provider_registered', 'info', {
 moduleId: provider.moduleId || moduleId,
 });
}

 if (menus.length || views.length) {
 remoteHealth.markOk(federatedName, moduleId);
 // A successful (re)load may have added the module's full menu tree —
 // bump the version so the sidebar re-renders and drops the degraded badge.
 setManifestVersion((v) => v + 1);
 }
 recordEvent('federation.loaded', 'info', {
 federatedName,
 moduleId,
 menus: menus.length,
 views: views.length,
 });
} catch (err) {
 remoteHealth.markFailed(federatedName, err, moduleId);
} finally {
 // Success or failure, this attempt has produced its answer — except on the
 // deferred path, where the import is still outstanding and will re-enter.
 if (!deferred) moduleRegistration.settle(moduleId);
}
};

 await Promise.all(
 uniqueModules.map(async (mod) => {
 const federatedName: string | undefined = mod.federatedModuleName;
 // Register a re-attempt so the operator-facing retry button and the
 // background scheduler can re-run THIS module's upgrade in isolation.
 if (federatedName) {
 remoteHealth.registerReattempt(federatedName, () => loadFederatedModule(mod));
 }
 await loadFederatedModule(mod);
 })
 );

 // Kick off the background re-attempt loop so a transiently-down remote
 // (dev server restart, brief network blip) self-heals without a reload.
 remoteHealth.startAutoRetry();

 // manifestLoaded was already flipped true after Pass 1 (the splash unmounts
 // as soon as the sidebar can render); Pass 2 only upgrades the registries in
 // place. Re-setting it here was a redundant no-op, so we just bump the version
 // to force the AppShell sidebar to re-render now that Pass 2 has populated
 // the full federated menu tree (parent + child views per module).
 setManifestVersion((v) => v + 1);
} catch (error) {
 const message = error instanceof Error ? error.message :'Failed to load manifest';
 setManifestError(message);
 recordError('manifest.load_failed', error, {});
}
};

 if (isAuthenticated && accessToken) {
 loadManifest();
}
 // tenantId is a dependency so a workspace switch reloads the manifest for
 // the new tenant. The loader already clears every registry; we also drop
 // the React Query cache and tear down SignalR so no prior-tenant data,
 // cached query result, or live subscription leaks across the switch
 // (multi-tenancy isolation). The SignalR effect below re-initializes once
 // manifestLoaded flips back to true for the new tenant.
}, [isAuthenticated, accessToken, tenantId]);

 // Cross-tenant isolation — when the active tenant changes, reset the shell
 // state that is scoped to a tenant: collapse to the splash and drop cached
 // query data. The manifest effect above then refetches; the realtime sockets
 // are owned by the shared HubConnectionProvider, which reconnects with the
 // new tenant's token and (via RealtimeInvalidationBridge unmount/remount on
 // the manifestLoaded flip) re-subscribes for the new tenant.
 const previousTenantRef = useRef<string | null>(tenantId);
 useEffect(() => {
 if (previousTenantRef.current === tenantId) return;
 previousTenantRef.current = tenantId;
 setManifestLoaded(false);
 // Drop per-remote health + the auto-retry loop so the prior tenant's
 // federation status (and its reattempt closures, which captured the old
 // module list) don't leak into the new tenant's session.
 remoteHealth.stopAutoRetry();
 remoteHealth.reset();
 // The prior tenant's in-flight registrations are meaningless for the new
 // one; the manifest reload below re-declares them.
 moduleRegistration.reset();
 queryClient.clear();
}, [tenantId]);

 if (!isAuthenticated) {
 return (
 <Router>
 <Routes>
 <Route path="/login" element={<LoginPage />} />
 {/* OAuth Authorization Code + PKCE return leg — exchanges ?code= for
 tokens BEFORE the user is considered authenticated. */}
 <Route path="/auth/callback" element={<AuthCallback />} />
 {/* Native email/password pre-session flows — reachable without a token. */}
 <Route path="/auth/signup" element={<SignupPage />} />
 <Route path="/auth/verify" element={<VerifyPage />} />
 <Route path="/auth/forgot" element={<ForgotPasswordPage />} />
 <Route path="/auth/reset" element={<ResetPasswordPage />} />
 <Route path="*" element={<Navigate to="/login" replace />} />
 </Routes>
 </Router>
 );
}

 if (!manifestLoaded) {
 return (
 <div className="flex items-center justify-center h-screen bg-surface-base">
 <div className="text-center max-w-md px-6">
 {manifestError ? (
 <>
 <h1 className="text-2xl font-semibold text-foreground mb-2 tracking-tight">
 Couldn't load Tyndo
 </h1>
 <p className="text-alarm-critical mb-4 text-sm">{manifestError}</p>
 <Button onClick={() => window.location.reload()}>
 Retry
 </Button>
 </>
 ) : (
 <>
 <div role="status" aria-label="Loading" className="animate-spin motion-reduce:animate-none rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4">
 <span className="sr-only">Loading…</span>
 </div>
 <p className="text-muted-foreground">Loading Tyndo…</p>
 </>
 )}
 </div>
 </div>
 );
}

 return (
 <>
 {/* Bridge core hub events to React Query cache invalidation over the
 shared HubConnectionProvider sockets. Only mounted once authed with a
 loaded manifest (the guards above return early otherwise), so it never
 subscribes before a tenant context exists. */}
 <RealtimeInvalidationBridge
 manifestSubscriptions={realtimeSubscriptions}
 tenantIdRef={tenantIdRef}
 />
 <Router>
 <Routes>
 <Route path="/login" element={<LoginPage />} />
 <Route path="/auth/callback" element={<AuthCallback />} />
 {/* Native auth flows remain routable post-session (e.g. an already
 signed-in user opening a verify/reset link from email). */}
 <Route path="/auth/signup" element={<SignupPage />} />
 <Route path="/auth/verify" element={<VerifyPage />} />
 <Route path="/auth/forgot" element={<ForgotPasswordPage />} />
 <Route path="/auth/reset" element={<ResetPasswordPage />} />
 <Route
 element={
 <ShellErrorBoundary scope="app-shell">
 <AppShell />
 </ShellErrorBoundary>
 }
 >
 <Route
 path="/"
 element={
 <LandingPageEditor routeKey="home">
 <PlantCanvas />
 </LandingPageEditor>
}
 />
 <Route
 path="/dashboard/:routeKey"
 element={
 <DashboardRoute />
}
 />
 <Route path="/browse" element={<HomePage />} />
 {/* Outcome-first persona workspaces. `/browse` is the module-first
  * catalogue ("what does Tyndo ship?"); these open on the named work
  * surfaces of one persona's own modules ("what am I here to do?").
  * The bare `/workspace` renders the same not-found state an unknown
  * persona does, so neither shape can fall through to the
  * `/:moduleId/:viewId` module host and render an empty page. */}
 <Route path="/workspace" element={<PersonaWorkspace />} />
 <Route path="/workspace/:persona" element={<PersonaWorkspace />} />
 <Route
 path="/widgets-admin"
 element={
 <WidgetsAdminRoute>
 <Suspense
 fallback={
 <div className="flex items-center justify-center h-96">
 <div role="status" aria-label="Loading" className="animate-spin motion-reduce:animate-none rounded-full h-12 w-12 border-b-2 border-primary">
 <span className="sr-only">Loading…</span>
 </div>
 </div>
}
 >
 <WidgetsAdminConsole />
 </Suspense>
 </WidgetsAdminRoute>
}
 />
 {/* EBR drilldown deep-links. The EBR widgets (batch-ui, virtual `ebr`
  * moduleId) navigate to `/ebr/...`, but EBR's UI surface lives inside
  * the Batch module — there is no `ebr` module manifest entry to resolve
  * the segment against. Bridge those paths onto the owning batch views
  * (preserving the query string) so the drilldowns land on the EBR
  * review workbench instead of an unresolvable-module empty state. */}
 <Route path="/ebr/*" element={<EbrRouteBridge />} />
 <Route path="/:moduleId" element={<ModuleRedirect />} />
 {/* Primary clean URL shape: /:moduleId/:viewId. */}
 <Route path="/:moduleId/:viewId" element={<ModuleView />} />
 <Route path="/:moduleId/:viewId/:entityId" element={<ModuleView />} />
 {/* Legacy URL shape kept for back-compat with bookmarked deep links
  * (older /view/ segment + /viewType/ entity routes). */}
 <Route path="/:moduleId/view" element={<ModuleRedirect />} />
 <Route path="/:moduleId/view/:viewId" element={<ModuleView />} />
 <Route path="/:moduleId/:viewType/:model" element={<ModuleView />} />
 <Route path="/:moduleId/:viewType/:model/:entityId" element={<ModuleView />} />
 </Route>
 </Routes>
 </Router>
 </>
 );
}

export default function App() {
 return (
 <ShellErrorBoundary scope="root">
 <TyndoProvider>
 <HubConnectionProvider>
 <QueryClientProvider client={queryClient}>
 {/* Effective permissions + roles for the signed-in principal.
  * `TyndoProvider` composes Auth/Tenant/Module only, and
  * `usePermissionsContext` falls back to an EMPTY permission set when
  * no provider is mounted — so with this missing, every
  * `usePermission(resource, action)` in every module answered "not
  * allowed", and every permission-gated affordance in the product
  * rendered permanently disabled-with-reason (approve, issue, sign,
  * release …) for every user including an administrator. It must sit
  * inside the QueryClientProvider because it resolves the set with a
  * react-query read of `GET /api/v1/auth/me`; the documented
  * `dev-token-*` local bypass short-circuits to wildcard without any
  * request. The gateway remains the authoritative enforcement point —
  * this only decides which controls are OFFERED. */}
 <PermissionsProvider>
 <ShellErrorBoundary scope="app-content">
 <AppContent />
 </ShellErrorBoundary>
 </PermissionsProvider>
 </QueryClientProvider>
 </HubConnectionProvider>
 </TyndoProvider>
 </ShellErrorBoundary>
 );
}
